import * as React from 'react';

import { logoVariants } from './Logo.styles';
import { cn } from '../../../lib/utils';

import type { LogoProps } from './Logo.types';

/**
 * Logo Component
 *
 * The Nabat AI brand logo - tree icon with "nabat.ai" wordmark.
 *
 * Design reference: Figma node 28606:84531
 * Original dimensions: 169px × 35px
 *
 * @example
 * ```tsx
 * // Default logo
 * <Logo />
 *
 * // Large size
 * <Logo size="lg" />
 *
 * // Inverted for dark backgrounds
 * <Logo inverted />
 * ```
 */
export const Logo = React.forwardRef<HTMLSpanElement, LogoProps>(
  ({ className, size = 'md', inverted = false, ariaLabel = 'Nabat AI Logo', ...props }, ref) => (
    <span
      ref={ref}
      role="img"
      aria-label={ariaLabel}
      className={cn(logoVariants({ size, inverted }), className)}
      {...props}
    >
      <svg
        viewBox="0 0 169 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-auto"
      >
        <path
          d="M54.7 12.3C53.3 11.3 51.4 10.7 49.2 10.7C47 10.7 45.1 11.2 43.7 12.3C42 13.5 41.2 15.2 41.2 17.3V29.1H46.2V17.3C46.2 16.9 46.3 16.6 46.7 16.4C47.3 16 48.1 15.8 49.3 15.8C50.5 15.8 51.3 16 51.9 16.4C52.2 16.6 52.3 16.9 52.3 17.3V27.1C52.3 27.6 52.5 28.1 52.9 28.5C53.3 28.9 53.7 29.1 54.3 29.1H57.3V17.3C57.3 15.2 56.5 13.6 54.8 12.3H54.7Z"
          fill="currentColor"
        />
        <path
          d="M75.4 11.1C74.4 11.1 73.5 11.5 72.9 12.2C71.4 11.1 69.5 10.6 67.4 10.7C65.3 10.8 63 11.8 61.4 13.4C59.8 15 58.7 17.4 58.7 19.9C58.7 22.4 59.7 24.8 61.4 26.6C63.1 28.4 65.4 29.3 68 29.3C70.6 29.3 71.4 28.8 72.8 27.8C73.4 28.5 74.2 28.9 75.3 28.9H77V11.1H75.3H75.4ZM71.2 23.2C70.3 24.1 69.3 24.5 68 24.5C66.7 24.5 65.7 24 64.9 23.2C64.1 22.4 63.7 21.4 63.7 20.2C63.7 19 64.1 17.9 64.9 17.1C65.7 16.3 66.8 15.8 68 15.8C69.2 15.8 70.2 16.2 71.1 17.1C72 18 72.4 19 72.4 20.2C72.4 21.4 72 22.4 71.1 23.3L71.2 23.2Z"
          fill="currentColor"
        />
        <path
          d="M89.5 10.7C88 10.7 86.7 11 85.5 11.7V4.9H80.5V29H82.2C83.2 29 84.1 28.6 84.7 27.9C86.2 29 87.9 29.5 89.9 29.4C91.9 29.3 94.6 28.3 96.2 26.6C97.8 24.9 98.9 22.6 98.9 20.1C98.9 17.6 98 15.3 96.2 13.5C94.4 11.7 92.2 10.8 89.6 10.8L89.5 10.7ZM92.6 23.2C91.7 24.1 90.7 24.5 89.5 24.5C88.3 24.5 87.2 24 86.4 23.2C85.6 22.4 85.1 21.3 85.1 20.1C85.1 18.9 85.6 17.8 86.4 17C87.2 16.2 88.3 15.7 89.5 15.7C90.7 15.7 91.8 16.2 92.6 17C93.4 17.8 93.9 18.9 93.9 20.1C93.9 21.3 93.4 22.4 92.6 23.2Z"
          fill="currentColor"
        />
        <path
          d="M116.3 11.1C115.3 11.1 114.4 11.5 113.8 12.2C112.3 11.1 110.4 10.6 108.3 10.7C106.2 10.8 103.9 11.8 102.3 13.4C100.7 15 99.6 17.4 99.6 19.9C99.6 22.4 100.6 24.8 102.3 26.6C104 28.4 106.3 29.3 108.9 29.3C111.5 29.3 112.3 28.8 113.7 27.8C114.3 28.5 115.1 28.9 116.2 28.9H117.9V11.1H116.2H116.3ZM112.1 23.2C111.2 24.1 110.2 24.5 108.9 24.5C107.6 24.5 106.6 24 105.8 23.2C105 22.4 104.6 21.4 104.6 20.2C104.6 19 105 17.9 105.8 17.1C106.6 16.3 107.7 15.8 108.9 15.8C110.1 15.8 111.1 16.2 112 17.1C112.9 18 113.3 19 113.3 20.2C113.3 21.4 112.9 22.4 112 23.3L112.1 23.2Z"
          fill="currentColor"
        />
        <path
          d="M157.2 11.1C156.2 11.1 155.3 11.5 154.7 12.2C153.2 11.1 151.3 10.6 149.2 10.7C147.1 10.8 144.8 11.8 143.2 13.4C141.4 15.2 140.5 17.4 140.5 19.9C140.5 22.4 141.5 24.8 143.2 26.6C144.9 28.4 147.2 29.3 149.8 29.3C152.4 29.3 153.2 28.8 154.6 27.8C155.2 28.5 156 28.9 157.1 28.9H158.8V11.1H157.1H157.2ZM153 23.2C152.1 24.1 151.1 24.5 149.8 24.5C148.5 24.5 147.5 24 146.7 23.2C145.9 22.4 145.5 21.4 145.5 20.2C145.5 19 145.9 17.9 146.7 17.1C147.5 16.3 148.6 15.8 149.8 15.8C151 15.8 152 16.2 152.9 17.1C153.8 18 154.2 19 154.2 20.2C154.2 21.4 153.8 22.4 152.9 23.3L153 23.2Z"
          fill="currentColor"
        />
        <path
          d="M164.6 10.6C165.4 10.6 166.1 10.3 166.7 9.7C167.3 9.1 167.6 8.4 167.6 7.6C167.6 6.8 167.3 6.1 166.7 5.5C166.1 4.9 165.4 4.6 164.6 4.6C163.8 4.6 163.1 4.9 162.5 5.5C161.9 6.1 161.6 6.8 161.6 7.6C161.6 8.4 161.9 9.1 162.5 9.7C163.1 10.3 163.8 10.6 164.6 10.6Z"
          fill="currentColor"
        />
        <path
          d="M130.2 24C130.2 24 130 24 130 24.1C129.7 24.3 128.2 24.4 127.7 24.4C127.2 24.4 126.6 24.3 126.3 24C126.1 23.8 126 23.4 126 22.8V16H128.4C128.9 16 129.4 15.8 129.8 15.4C130.2 15 130.4 14.6 130.4 14V11H126.1V9.4C126.1 8.9 126 8.4 125.7 8C125 6.9 124 6.4 122.9 6.4H121.2V22.8C121.2 24.7 121.8 26.3 123.1 27.5C124.3 28.7 125.9 29.3 127.9 29.3C129.9 29.3 129.7 29.3 130.5 29.1V23.8L130.2 24Z"
          fill="currentColor"
        />
        <path d="M167 11.6H162V29.2H167V11.6Z" fill="currentColor" />
        <path
          d="M139.6 26.1C139.6 27.9 138.1 29.4 136.3 29.4C134.5 29.4 133.7 29.2 133.5 29.2C133.5 29.2 133 27 133 26.1C133 24.3 134.5 22.8 136.3 22.8C138.1 22.8 139.1 22.9 139.3 23.2C139.5 23.6 139.7 25.2 139.7 26.2L139.6 26.1Z"
          fill="currentColor"
        />
        <path
          d="M29.3 17.5C27.4 17.5 25.8 18.8 25.4 20.5H25.1C22.3 20.5 19.8 21.7 18.1 23.6V18.7C18.1 15.4 18.6 14.2 21.3 14.1C21.8 15.7 23.3 16.8 25.1 16.8C26.9 16.8 29.1 15 29.1 12.8C29.1 10.6 27.3 8.8 25.1 8.8C22.9 8.8 21.8 10 21.3 11.7C20 11.7 19 12 18.2 12.4V7.8C19.8 7.3 20.9 5.8 20.9 4C20.9 2.2 19 0.200001 16.8 0.200001C14.6 0.200001 12.8 2 12.8 4.2C12.8 6.4 14 7.5 15.7 8V12.6C14.9 12.1 13.8 11.9 12.4 11.8C11.9 10.1 10.4 8.9 8.6 8.9C6.8 8.9 4.6 10.7 4.6 12.9C4.6 15.1 6.4 16.9 8.6 16.9C10.8 16.9 11.8 15.8 12.4 14.2C15.2 14.2 15.7 15.3 15.7 18.8V23.7C14 21.8 11.5 20.6 8.7 20.6H8C7.6 18.9 6 17.6 4.1 17.6C2.2 17.6 0 19.3 0 21.5C0 23.7 1.8 25.5 4 25.5C6.2 25.5 7.1 24.5 7.7 23H8.6C12.5 23 15.6 26.1 15.6 30V31.6H11.4C10.7 31.6 10.2 32.1 10.2 32.8C10.2 33.5 10.7 34 11.4 34H22.3C23 34 23.5 33.5 23.5 32.8C23.5 32.1 23 31.6 22.3 31.6H18.1V30C18.1 26.1 21.3 23 25.1 23H25.6C26.2 24.5 27.6 25.5 29.3 25.5C31 25.5 33.3 23.7 33.3 21.5C33.3 19.3 31.5 17.5 29.3 17.5ZM25 11.4C25.9 11.4 26.6 12.1 26.6 13C26.6 13.9 25.9 14.6 25 14.6C24.1 14.6 23.4 13.9 23.4 13C23.4 12.1 24.1 11.4 25 11.4ZM8.6 14.5C7.7 14.5 7 13.8 7 12.9C7 12 7.7 11.3 8.6 11.3C9.5 11.3 10.2 12 10.2 12.9C10.2 13.8 9.5 14.5 8.6 14.5ZM4.1 23.1C3.2 23.1 2.5 22.4 2.5 21.5C2.5 20.6 3.2 19.9 4.1 19.9C5 19.9 5.7 20.6 5.7 21.5C5.7 22.4 5 23.1 4.1 23.1ZM16.8 5.7C15.9 5.7 15.2 5 15.2 4.1C15.2 3.2 15.9 2.5 16.8 2.5C17.7 2.5 18.4 3.2 18.4 4.1C18.4 5 17.7 5.7 16.8 5.7ZM29.3 23.1C29.3 23.1 29.2 23.1 29.1 23.1C28.3 23 27.8 22.3 27.8 21.6C27.8 20.9 28.4 20.1 29.2 20C30.1 20 30.8 20.7 30.8 21.6C30.8 22.5 30.1 23.2 29.2 23.2L29.3 23.1Z"
          fill="currentColor"
        />
      </svg>
    </span>
  )
);

Logo.displayName = 'Logo';
